// NeoCore · Cumplimiento › OkID · Configuración (ADR 023)
// -----------------------------------------------------------------------------
// Interruptores para prender/apagar la verificación con OkID sin build ni deploy:
//   · Actualización de datos (hoja al abrir la app): Apagado / Piloto (NeoIDs) / Todos
//   · Inscripción de asociados nuevos (autorización + selfie + OkID): Prendido / Apagado
// Muestra el uso y el costo estimado del mes (costo por consulta en parametro_sistema · okid.costo_consulta, hoy Q4.00).
// Backend: okid_config / okid_config_set (migración 0382). Solo admin puede cambiar.
import { useCallback, useEffect, useState } from 'react'
import { ScanFace } from 'lucide-react'
import { supabase } from '@/lib/supabase'

type Config = {
  actualizacion: string
  inscripcion: boolean
  bloquear: boolean
  validar_dpi_telefono: boolean
  dias_gracia: number
  hoy: { verificaciones: number; por_decision: Record<string, number>; altas: number; vieron_actualizacion: number; completaron: number; bloqueados: number }
  costo_por_consulta: number
  mes: { verificaciones: number; por_origen: Record<string, number>; aprobadas: number; casos_aml_abiertos: number }
  actualizaciones: { completadas: number; pospuestas: number; pendientes_vistas: number; verificados: number; activos: number }
}
type Modo = 'off' | 'piloto' | 'todos'
type FilaAct = {
  asociado_id: string; neo_id: string; nombre: string; telefono: string; estado: string
  vio_at: string | null; completada_at: string | null; fecha_limite: string | null
  decision_okid: string | null; match_pct: number | null; verificada: boolean
  email: string | null; ref_nombre: string | null; ref_telefono: string | null; ref_relacion: string | null
}
const fFecha = (s: string | null) => s ? new Date(s).toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
const DEC: Record<string, [string, string]> = { pass: ['Aprobada', '#15803D'], review: ['En revisión', '#B45309'], fail: ['Rechazada', '#B42318'] }

const card: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 14, padding: 18, background: 'var(--card, #fff)' }
const muted: React.CSSProperties = { fontSize: 13, color: 'var(--muted-foreground)' }

function Segmentado({ valor, opciones, onChange, disabled }: { valor: string; opciones: [string, string][]; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <div style={{ display: 'inline-flex', background: 'var(--muted, #f4f4f5)', borderRadius: 10, padding: 3, gap: 2 }}>
      {opciones.map(([v, l]) => (
        <button key={v} disabled={disabled} onClick={() => onChange(v)}
          style={{ border: 0, borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
            background: valor === v ? 'var(--card, #fff)' : 'transparent', boxShadow: valor === v ? '0 1px 2px rgba(0,0,0,.08)' : 'none',
            color: valor === v ? (v === 'off' || v === 'false' ? '#52525b' : 'var(--brand, #ff5b04)') : 'var(--muted-foreground)' }}>
          {l}
        </button>
      ))}
    </div>
  )
}

export function NeoCoreOkidConfigPage() {
  const [cfg, setCfg] = useState<Config | null>(null)
  const [piloto, setPiloto] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null)
  const [filas, setFilas] = useState<FilaAct[]>([])
  const [filtro, setFiltro] = useState<'todas' | 'completada' | 'pendiente'>('todas')

  const cargar = useCallback(async () => {
    const { data, error } = await supabase.rpc('okid_config')
    if (error) { setMsg({ ok: false, t: error.message }); return }
    const c = data as Config
    setCfg(c)
    const a = (c.actualizacion ?? 'off').toLowerCase()
    setPiloto(a === 'off' || a === 'todos' ? '' : a)
    const { data: l } = await supabase.rpc('actualizaciones_datos_lista', { p_limit: 500 })
    setFilas((l ?? []) as FilaAct[])
  }, [])
  useEffect(() => { void cargar() }, [cargar])

  const guardar = async (cambios: { p_actualizacion?: string; p_inscripcion?: boolean; p_bloquear?: boolean; p_validar_dpi?: boolean }, confirmar?: string) => {
    if (confirmar && !confirm(confirmar)) return
    setBusy(true); setMsg(null)
    const { data, error } = await supabase.rpc('okid_config_set', cambios)
    setBusy(false)
    if (error) { setMsg({ ok: false, t: error.message }); return }
    setCfg(data as Config)
    setMsg({ ok: true, t: 'Guardado. La app lo toma la próxima vez que el asociado abre la pantalla.' })
  }

  if (!cfg) return <div style={{ padding: 24, ...muted }}>{msg?.t ?? 'Cargando…'}</div>

  const a = cfg.actualizacion.toLowerCase()
  const modo: Modo = a === 'off' || a === '' || a === 'false' ? 'off' : a === 'todos' ? 'todos' : 'piloto'
  const costo = cfg.mes.verificaciones * Number(cfg.costo_por_consulta)
  const pendientes = Math.max(0, cfg.actualizaciones.activos - cfg.actualizaciones.verificados)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 880 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <ScanFace size={26} />
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>OkID · Configuración</h1>
          <p style={{ ...muted, margin: '2px 0 0' }}>Prendé o apagá la verificación de identidad con RENAP sin publicar una versión nueva de la app.</p>
        </div>
      </div>

      {/* Hoy: para monitorear pruebas y decidir si apagar algo */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Hoy</div>
          <button onClick={() => void cargar()} style={{ border: '1px solid var(--border)', background: 'transparent', borderRadius: 8, padding: '5px 10px', fontSize: 12.5, cursor: 'pointer' }}>Actualizar</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginTop: 10 }}>
          {[
            ['Consultas OkID', cfg.hoy.verificaciones],
            ['Aprobadas', cfg.hoy.por_decision.pass ?? 0],
            ['En revisión', cfg.hoy.por_decision.review ?? 0],
            ['Rechazadas / error', (cfg.hoy.por_decision.fail ?? 0) + (cfg.hoy.por_decision.sin_decision ?? 0) + (cfg.hoy.por_decision.error ?? 0)],
            ['Altas nuevas', cfg.hoy.altas],
            ['Vieron la actualización', cfg.hoy.vieron_actualizacion],
            ['Completaron', cfg.hoy.completaron],
            ['Bloqueados (plazo vencido)', cfg.hoy.bloqueados],
          ].map(([l, v]) => (
            <div key={String(l)} style={{ background: 'var(--muted, #f4f4f5)', borderRadius: 10, padding: 12 }}>
              <div style={muted}>{l}</div><div style={{ fontSize: 20, fontWeight: 700 }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {msg && <div style={{ ...card, padding: 12, fontSize: 13, background: msg.ok ? '#ecfdf5' : '#fef2f2', color: msg.ok ? '#065f46' : '#991b1b' }}>{msg.t}</div>}

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Actualización de datos</div>
            <div style={{ ...muted, marginTop: 4 }}>Hoja "Mantengamos tu cuenta al día" al abrir la app: autorización, frente del DPI, selfie con OkID, correo y referencia. Apagado = la hoja deja de aparecer y no bloquea a nadie.</div>
          </div>
          <Segmentado valor={modo} disabled={busy}
            opciones={[['off', 'Apagado'], ['piloto', 'Piloto'], ['todos', 'Todos']]}
            onChange={(v) => {
              if (v === 'off') void guardar({ p_actualizacion: 'off' })
              else if (v === 'todos') void guardar({ p_actualizacion: 'todos' }, `Se le va a ofrecer a ${pendientes} asociados sin verificar. Costo máximo estimado: Q${(pendientes * Number(cfg.costo_por_consulta)).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. ¿Confirmás?`)
              else void guardar({ p_actualizacion: piloto || '126500' })
            }} />
        </div>
        {modo === 'piloto' && (
          <div style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12.5, fontWeight: 600 }}>NeoIDs del piloto</label>
            <input value={piloto} onChange={(e) => setPiloto(e.target.value.replace(/\s/g, ''))} placeholder="126500,288499"
              style={{ flex: 1, minWidth: 220, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontFamily: 'var(--font-mono, monospace)' }} />
            <button disabled={busy || !piloto} onClick={() => void guardar({ p_actualizacion: piloto })}
              style={{ padding: '8px 14px', borderRadius: 8, border: 0, background: 'var(--brand, #ff5b04)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Guardar piloto</button>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 16 }}>
          {[
            ['Completaron', cfg.actualizaciones.completadas],
            ['Vieron y no completaron', cfg.actualizaciones.pendientes_vistas],
            ['Identidad verificada', `${cfg.actualizaciones.verificados} / ${cfg.actualizaciones.activos}`],
          ].map(([l, v]) => (
            <div key={String(l)} style={{ background: 'var(--muted, #f4f4f5)', borderRadius: 10, padding: 12 }}>
              <div style={muted}>{l}</div><div style={{ fontSize: 20, fontWeight: 700 }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Inscripción de asociados nuevos</div>
            <div style={{ ...muted, marginTop: 4 }}>Después del recibo, la app pide la autorización y la selfie; OkID corre al crear el asociado. Si no autoriza, sigue sin verificación. Apagado = la inscripción queda como hoy.</div>
          </div>
          <Segmentado valor={cfg.inscripcion ? 'true' : 'false'} disabled={busy}
            opciones={[['false', 'Apagado'], ['true', 'Prendido']]}
            onChange={(v) => void guardar({ p_inscripcion: v === 'true' },
              v === 'true' ? `Cada alta nueva va a consumir una consulta OkID (Q${Number(cfg.costo_por_consulta).toFixed(2)}). ¿Confirmás?` : undefined)} />
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Bloquear operaciones si no actualiza</div>
            <div style={{ ...muted, marginTop: 4 }}>Pasados {cfg.dias_gracia} días desde que vio la hoja, no puede retirar ni pedir créditos hasta actualizar (ADR 024). Apagado = la hoja sigue apareciendo pero sin fecha límite ni bloqueo.</div>
          </div>
          <Segmentado valor={cfg.bloquear ? 'true' : 'false'} disabled={busy}
            opciones={[['false', 'Apagado'], ['true', 'Prendido']]}
            onChange={(v) => void guardar({ p_bloquear: v === 'true' },
              v === 'true' ? `Los asociados que no actualicen en ${cfg.dias_gracia} días no van a poder retirar ni pedir créditos. ¿Confirmás?` : undefined)} />
        </div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Validar la foto del DPI en el teléfono</div>
            <div style={{ ...muted, marginTop: 4 }}>La app lee el DPI (texto, número y cara) antes de aceptar la foto. Si da problemas en algunos teléfonos, apagalo: la foto pasa directo y la valida el servidor y OkID. Se aplica en menos de un minuto, sin actualizar la app.</div>
          </div>
          <Segmentado valor={cfg.validar_dpi_telefono ? 'true' : 'false'} disabled={busy}
            opciones={[['false', 'Apagado'], ['true', 'Prendido']]}
            onChange={(v) => void guardar({ p_validar_dpi: v === 'true' })} />
        </div>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10 }}>Uso del mes</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          {[
            ['Consultas OkID', cfg.mes.verificaciones],
            ['Costo estimado', `Q${costo.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
            ['Aprobadas', cfg.mes.aprobadas],
            ['Casos AML abiertos', cfg.mes.casos_aml_abiertos],
          ].map(([l, v]) => (
            <div key={String(l)} style={{ background: 'var(--muted, #f4f4f5)', borderRadius: 10, padding: 12 }}>
              <div style={muted}>{l}</div><div style={{ fontSize: 20, fontWeight: 700 }}>{v}</div>
            </div>
          ))}
        </div>
        {Object.keys(cfg.mes.por_origen).length > 0 && (
          <div style={{ ...muted, marginTop: 10 }}>
            Por origen: {Object.entries(cfg.mes.por_origen).map(([o, n]) => `${o.replace('_app', '').replace('_', ' ')} ${n}`).join(' · ')}
          </div>
        )}
      </div>

      {/* Actualizaciones de datos: correo confirmado, referencia personal y resultado OkID */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Actualizaciones de datos</div>
            <div style={muted}>Correo confirmado, referencia personal y resultado de OkID por asociado.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Segmentado valor={filtro} opciones={[['todas', 'Todas'], ['completada', 'Completadas'], ['pendiente', 'Pendientes']]} onChange={(v) => setFiltro(v as typeof filtro)} />
            <button onClick={() => {
              const rows = filas.filter((f) => filtro === 'todas' || (filtro === 'completada' ? f.estado === 'completada' : f.estado !== 'completada'))
              const head = ['NeoID', 'Nombre', 'Teléfono', 'Estado', 'Vio', 'Completó', 'Fecha límite', 'OkID', 'Match %', 'Correo', 'Referencia', 'Tel. referencia', 'Relación']
              const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
              const csv = [head, ...rows.map((f) => [f.neo_id, f.nombre, f.telefono, f.estado, f.vio_at, f.completada_at, f.fecha_limite, f.decision_okid, f.match_pct, f.email, f.ref_nombre, f.ref_telefono, f.ref_relacion])].map((r) => r.map(esc).join(',')).join('\n')
              const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv' })); a.download = 'actualizaciones-datos.csv'; a.click()
            }} style={{ border: '1px solid var(--border)', background: 'transparent', borderRadius: 8, padding: '6px 10px', fontSize: 12.5, cursor: 'pointer' }}>Exportar CSV</button>
          </div>
        </div>
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--muted-foreground)' }}>
                {['Asociado', 'Estado', 'OkID', 'Correo', 'Referencia', 'Fechas'].map((h) => <th key={h} style={{ padding: '8px 6px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filas.filter((f) => filtro === 'todas' || (filtro === 'completada' ? f.estado === 'completada' : f.estado !== 'completada')).map((f) => {
                const d = f.decision_okid ? DEC[f.decision_okid] : null
                return (
                  <tr key={f.asociado_id} style={{ borderBottom: '1px solid var(--border)', verticalAlign: 'top' }}>
                    <td style={{ padding: '8px 6px' }}>
                      <a href={`/neocore/asociados/${f.asociado_id}`} style={{ fontWeight: 600, color: 'inherit' }}>{f.nombre}</a>
                      <div style={muted}>{f.neo_id} · {f.telefono || '—'}</div>
                    </td>
                    <td style={{ padding: '8px 6px' }}>{f.estado === 'completada' ? '✅ Completada' : f.estado === 'pospuesta' ? 'Pospuesta' : 'Pendiente'}</td>
                    <td style={{ padding: '8px 6px' }}>{d ? <span style={{ color: d[1], fontWeight: 600 }}>{d[0]}{f.match_pct != null ? ` · ${Math.round(Number(f.match_pct))}%` : ''}</span> : '—'}</td>
                    <td style={{ padding: '8px 6px' }}>{f.email || '—'}</td>
                    <td style={{ padding: '8px 6px' }}>{f.ref_nombre ? <>{f.ref_nombre}<div style={muted}>{f.ref_relacion} · {f.ref_telefono}</div></> : '—'}</td>
                    <td style={{ padding: '8px 6px', ...muted }}>Vio: {fFecha(f.vio_at)}<br />Completó: {fFecha(f.completada_at)}{f.estado !== 'completada' && f.fecha_limite ? <><br />Límite: {fFecha(f.fecha_limite)}</> : null}</td>
                  </tr>
                )
              })}
              {filas.length === 0 && <tr><td colSpan={6} style={{ padding: 14, ...muted }}>Todavía no hay actualizaciones.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
